<template>
  <section class="main">
    <div class="main__content">
      <template v-if="loaded">
        <back-link v-if="belongsToUser" :to="`/my/repos`" class="back-link">
          {{ $t('MY_ALTSTORE_REPOSITORIES') }}
        </back-link>
        <back-link v-else :to="`/repos`" class="back-link">
          {{ $t('PUBLIC_ALTSTORE_REPOS') }}
        </back-link>
      </template>
      <apps class="main__apps" :heading="name" :list="apps" type="repo">
        <template v-if="loaded && !apps.new.length" #new>
          <p>{{ $t('NO_APPS_IN_REPO') }}</p>
        </template>
      </apps>
    </div>

    <aside class="main__sidebar">
      <div class="block block_yellow">
        <div class="block__top">
          <div class="block__left">
            <img src="@/assets/icon-news-sidebar.svg" />
          </div>
          <div class="block__right">
            <h2 class="block__heading">{{ $t("REPOSITORY_NEWS") }}</h2>
            <ul class="block__list" v-if="!loaded">
              <li class="block__item block__item_stub" v-for="n in 3" :key="n">
                n
              </li>
            </ul>
            <p v-else-if="!news.length">
              {{ $t('NO_NEWS_IN_REPO') }}
            </p>
            <ul v-else class="news">
              <li v-for="item in news" :key="item.id" class="block__item">
                <span class="news__title">{{ item.title }}</span>
                <span class="news__caption">{{ item.caption }}</span>
                <span class="news__date">{{ new Date(item.date).toLocaleDateString() }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </aside>
  </section>
</template>

<script>
import Apps from '@/components/Apps';
import BackLink from '@/components/BackLink';

export default {
  components: { Apps, BackLink },
  computed: {
    title() {
      return this.$t('SITE_TITLE');
    },
    id() {
      return this.$route.params.id;
    },
  },
  data() {
    return {
      name: '',
      apps: {
        new: [],
        top: [],
      },
      news: [],
      belongsToUser: true,
      loaded: false,
    };
  },
  methods: {
    getRepo() {
      this.$api.getAltstoreRepo(this.id)
        .then((response) => {
          if (!response.success) {
            throw response;
          }

          const { contents } = response.data[0];
          this.belongsToUser = response.data[0].is_yours === 1;
          this.name = contents.name;
          this.apps.new = contents.apps;
          this.news = contents.news.sort((a, b) => new Date(b.date) - new Date(a.date));
        })
        .catch((response) => {
          this.$router.replace({
            name: 'error',
            params: {
              message: response.errors[0].translated,
              link: '/my/repos',
              linkText: 'MY_ALTSTORE_REPOSITORIES',
            },
          });
        })
        .finally(() => {
          this.loaded = true;
        });
    },
  },
  created() {
    this.getRepo();
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/variables";

.main {
  display: flex;

  @include notebook {
    flex-direction: column-reverse;
  }

  &__content {
    width: 100%;
    max-width: 800px;

    @include notebook {
      max-width: 100%;
    }
  }

  &__sidebar {
    width: 370px;
    margin-left: auto;

    @include notebook {
      margin-left: 0;
      width: 100%;
    }
  }

  &__apps {
    &:not(:last-child) {
      margin-bottom: 40px;

      @include notebook {
        margin-bottom: 30px;
      }
    }
  }
}

.block {
  padding: 15px;
  border-radius: 10px;
  margin-bottom: 15px;
  overflow: hidden;
  position: relative;

  @include notebook {
    padding: 10px;
  }

  &_yellow {
    background-image: var(--yellow-gradient);
    border: 1px solid var(--yellow-border);
  }

  &_purple {
    background-image: var(--purple-gradient);
    border: 1px solid var(--purple-border);
    max-height: 140px;
  }

  &_devices {
    min-height: 120px;
  }

  &__top,
  &__bottom {
    display: flex;
  }

  &__right {
    margin-left: 15px;
    min-width: 0;
    flex-grow: 1;
  }

  &__heading {
    font-size: 20px;
    color: var(--text-primary);
    font-weight: 600;
    margin-bottom: 10px;

    &-icon {
      margin: 0 4px;
      filter: var(--img-filter);
    }

    &_small {
      display: flex;
      font-size: 16px;
    }
  }

  &__item {
    line-height: 16px;

    &_stub {
      background-color: var(--stub-bg);
      border-radius: 6px;
      color: transparent;
      display: block;
    }

    &:not(:last-child) {
      margin-bottom: 10px;
    }
  }

  &__link {
    color: $primary;
  }

  &__more {
    display: flex;
    align-items: center;
    margin-top: 15px;
    margin-left: auto;
    color: var(--text-tertiary);
    font-weight: 600;
    font-size: 12px;
    white-space: nowrap;

    &-icon {
      height: 9px;
      margin-top: 1px;
      margin-left: 5px;
    }
  }

  &__text {
    color: var(--text-primary);
  }

  &__link-device {
    display: inline-block;
    margin-top: 20px;
    background: var(--bg);
    border: 1px solid lighten($primary, 35);
    border-radius: 2px;
    padding: 5px 10px;
    color: $primary;
    font-weight: 500;
  }

  &__device-desc {
    margin-bottom: 20px;
  }

  &__device-page {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    background: var(--bg-transparent);
    box-shadow: 0 -1px 4px 0 rgba(203, 203, 203, 0.3);
    padding: 10px 15px;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    color: var(--text-tertiary);
    font-weight: 600;
    font-size: 12px;
  }
}

.news {
  max-height: 350px;
  overflow: scroll;
  mask-image: linear-gradient(to bottom, transparent, #000 8% 92%, transparent);
  padding-top: 18px;
  margin-top: -15px;
  padding-bottom: 20px;

  @include notebook {
    max-height: 200px;
  }

  &__title,
  &__caption,
  &__date {
    display: block;
  }

  &__title {
    font-weight: 700;
  }

  &__caption {
    margin: 3px 0;
  }

  &__date {
    font-size: 12px;
    font-weight: 700;
    opacity: .4;
  }
}

.back-link {
  margin-bottom: 15px;
}
</style>
